import { useParams } from 'react-router-dom';
import { ArrowRight, BookOpenText, ClipboardList, Download, ExternalLink, FileSpreadsheet, FileText, Info, Library, Scale, ShieldCheck, Video } from 'lucide-react';
import { PageStructuredData } from '@/components/seo/PageStructuredData';
import { StructuredData } from '@/components/seo/StructuredData';
import { CheckList, CtaPanel, DataTable, FaqSection, InShort, PageHero, PageShell, RelatedLinks, Reveal, Section, SectionHeading, SmartLink, StepFlow } from '@/components/page/PageKit';
import { Button } from '@/components/ui/button';
import { APP_URL } from '@/data/marketingContent';
import { getTemplate, questionCount, type TemplateDoc } from '@/data/templates';
import { articleSchema, faqSchema } from '@/seo/schemas';
import { useSEO } from '@/hooks/useSEO';
import NotFound from '@/pages/NotFound';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const formatDate = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

const RELATED_ICONS = [ClipboardList, BookOpenText, Scale, Library];

function DownloadButton({ t, tone = 'light' }: { t: TemplateDoc; tone?: 'light' | 'dark' }) {
  const label = t.file.format === 'CSV' ? 'Download the free template (CSV)' : 'Download the free template (Markdown)';
  return (
    <Button asChild size="lg" className={tone === 'dark' ? 'h-[54px] rounded-xl bg-white px-7 text-base font-semibold text-bv-ink hover:bg-white/90' : 'bv-shine h-[54px] rounded-xl px-7 text-base font-semibold'}>
      <a href={t.file.href} download><Download className="h-4 w-4" /> {label}</a>
    </Button>
  );
}

function QuestionGroups({ t }: { t: TemplateDoc }) {
  let n = 0;
  return (
    <div className="mt-12 space-y-14">
      {t.groups!.map(group => (
        <div key={group.name}>
          <Reveal>
            <h3 className="font-display text-2xl font-semibold text-bv-ink">{group.name}</h3>
            <p className="mt-2 max-w-3xl text-[15.5px] leading-7 text-bv-ink2">{group.intro}</p>
          </Reveal>
          <ol className="mt-6 grid gap-4 lg:grid-cols-2">
            {group.questions.map(q => {
              n += 1;
              return (
                <li key={q.q} className="flex h-full flex-col rounded-2xl border border-bv-blue-line bg-white p-5 sm:p-6">
                  <div className="flex gap-3">
                    <span aria-hidden className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-bv-blue-wash text-sm font-semibold text-bv-blue">{n}</span>
                    <p className="font-display text-[17px] font-semibold leading-snug text-bv-ink"><span className="sr-only">Question {n}: </span>{q.q}</p>
                  </div>
                  <dl className="mt-4 space-y-2.5 text-[14.5px] leading-6">
                    <div><dt className="inline font-semibold text-bv-ink">Why ask it: </dt><dd className="inline text-bv-ink2">{q.why}</dd></div>
                    <div><dt className="inline font-semibold text-bv-ink">A strong answer shows: </dt><dd className="inline text-bv-ink2">{q.listen}</dd></div>
                    {q.followUp && <div><dt className="inline font-semibold text-bv-ink">Follow-up: </dt><dd className="inline text-bv-ink2">{q.followUp}</dd></div>}
                  </dl>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </div>
  );
}

/** Where a page sits: the template library by default, or another section such as interview questions by role. */
export type TemplateSection = { name: string; href: string; allLabel: string; stepsTitle: (t: TemplateDoc) => string };

const TEMPLATE_SECTION: TemplateSection = { name: 'Templates', href: '/templates', allLabel: 'See all templates', stepsTitle: t => `How to use the ${t.name.toLowerCase()} template` };

export function TemplatePage({ t, section = TEMPLATE_SECTION }: { t: TemplateDoc; section?: TemplateSection }) {
  const url = `https://beatview.ai${section.href}/${t.slug}`;
  const headline = `${t.h1[0]} ${t.h1[1]}`;
  const count = questionCount(t);
  useSEO({ title: t.seoTitle, description: t.description, canonicalUrl: url, ogType: 'article' });

  const crumbs = [{ name: section.name, href: section.href }, { name: t.name }];
  const related = [
    ...t.related.slugs.map(slug => getTemplate(slug)).filter((r): r is TemplateDoc => Boolean(r)).map(r => ({ title: r.name, body: r.description.split('.')[0] + '.', href: `/templates/${r.slug}` })),
    ...t.related.links,
  ].slice(0, 4).map((item, i) => ({ ...item, icon: RELATED_ICONS[i % RELATED_ICONS.length] }));

  return (
    <PageShell>
      <PageStructuredData name={headline} description={t.description} url={url} dateModified={t.updated} breadcrumbs={[{ name: section.name, url: `https://beatview.ai${section.href}` }, { name: t.name }]} />
      <StructuredData data={[
        articleSchema({ headline, description: t.description, url, datePublished: t.published, dateModified: t.updated, about: { '@type': 'DigitalDocument', name: `${t.name} template`, url: `https://beatview.ai${t.file.href}`, encodingFormat: t.file.format === 'CSV' ? 'text/csv' : 'text/markdown', isAccessibleForFree: true } }),
        faqSchema(t.faqs),
      ]} />
      <article>
        <PageHero breadcrumbs={crumbs} eyebrow={`Free template · Updated ${formatDate(t.updated)}`} title={<>{t.h1[0]} <span className="bv-hero-highlight">{t.h1[1]}</span></>}>
          <InShort label="The short answer">{t.shortAnswer}</InShort>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <DownloadButton t={t} />
            <Button asChild size="lg" variant="outline" className="h-[54px] rounded-xl border-bv-blue-line bg-white px-7 text-base font-semibold text-bv-ink hover:bg-bv-blue-wash"><SmartLink href="#run-it-in-beatview">{t.beatview.label ?? 'Run it in Beatview'} <ArrowRight className="h-4 w-4" /></SmartLink></Button>
          </div>
          <p className="mt-4 max-w-2xl text-[14px] font-medium text-bv-ink2">{t.file.summary} Free to download and adapt; no sign-up needed.</p>
        </PageHero>

        <Section tone="white" narrow labelledBy="when-title">
          <SectionHeading id="when-title" eyebrow="When to use it" title={`When to use ${count ? 'these questions' : 'this template'}`} />
          <Reveal delay={0.05}>
            <div className="mt-6 space-y-4 text-[16.5px] leading-8 text-bv-ink2">
              {t.whenToUse.map(p => <p key={p.slice(0, 40)}>{p}</p>)}
            </div>
          </Reveal>
        </Section>

        {t.groups && t.groupsHeading && (
          <Section tone="wash" id="questions" labelledBy="questions-title">
            <SectionHeading id="questions-title" eyebrow={`${count} questions`} title={t.groupsHeading.title} description={t.groupsHeading.description} />
            <QuestionGroups t={t} />
          </Section>
        )}

        {t.rubric && (
          <Section tone="wash" id="rubric" labelledBy="rubric-title">
            <SectionHeading id="rubric-title" eyebrow="Example rubric" title={t.rubric.title} description={t.rubric.description} />
            <Reveal delay={0.1} className="mt-10">
              <DataTable caption={t.rubric.title} columns={['Criterion', 'What it means', 'Score 1 looks like', 'Score 3 looks like', 'Score 5 looks like']} rows={t.rubric.rows.map(r => [r.criterion, r.definition, r.low, r.mid, r.high])} minWidth={980} />
            </Reveal>
          </Section>
        )}

        {t.form && (
          <Section tone="wash" id="form" labelledBy="form-title">
            <SectionHeading id="form-title" eyebrow="The form" title={t.form.title} description={t.form.description} />
            <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {t.form.sections.map((section, i) => (
                <Reveal key={section.name} delay={(i % 3) * 0.05}>
                  <div className="flex h-full flex-col rounded-3xl border border-bv-blue-line bg-white p-6">
                    <h3 className="font-display text-lg font-semibold text-bv-ink">{section.name}</h3>
                    <p className="mt-1 text-[14px] text-bv-mute">{section.intro}</p>
                    <dl className="mt-4 space-y-3 text-[14.5px] leading-6">
                      {section.fields.map(f => <div key={f.label}><dt className="font-semibold text-bv-ink">{f.label}</dt><dd className="text-bv-ink2">{f.hint}</dd></div>)}
                    </dl>
                  </div>
                </Reveal>
              ))}
            </div>
          </Section>
        )}

        {t.policy && (
          <Section tone="wash" id="policy" labelledBy="policy-title">
            <SectionHeading id="policy-title" eyebrow="The template" title={t.policy.title} description={t.policy.description} />
            <ol className="mt-10 grid gap-4 md:grid-cols-2">
              {t.policy.sections.map((section, i) => (
                <li key={section.heading} className="rounded-2xl border border-bv-blue-line bg-white p-6">
                  <h3 className="font-display text-lg font-semibold text-bv-ink"><span className="mr-2 text-bv-blue">{i + 1}.</span>{section.heading}</h3>
                  <p className="mt-2 text-[15px] leading-7 text-bv-ink2">{section.body}</p>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {t.scale && (
          <Section tone="white" id="scoring" labelledBy="scale-title">
            <SectionHeading id="scale-title" eyebrow="Scoring" title={t.scale.title} description={t.scale.description} />
            <Reveal delay={0.1} className="mt-10">
              <DataTable caption={t.scale.title} columns={['Score', 'Level', 'What it means']} rows={t.scale.levels.map(l => [String(l.score), l.label, l.description])} minWidth={640} />
            </Reveal>
          </Section>
        )}

        {t.examples && (
          <Section tone="white" labelledBy="examples-title">
            <SectionHeading id="examples-title" eyebrow="Examples" title={t.examples.title} description={t.examples.description} />
            <Reveal delay={0.1} className="mt-10">
              <DataTable caption={t.examples.title} columns={['Instead of', 'Write']} rows={t.examples.pairs.map(p => [p.weak, p.strong])} minWidth={640} />
            </Reveal>
          </Section>
        )}

        <Section tone="base" labelledBy="steps-title">
          <SectionHeading id="steps-title" eyebrow="How to use it" title={section.stepsTitle(t)} />
          <div className="mt-12"><StepFlow steps={t.steps} /></div>
        </Section>

        <Section tone="white" narrow labelledBy="avoid-title">
          <SectionHeading id="avoid-title" eyebrow="Watch out" title={t.avoid.title} />
          <Reveal delay={0.05}><CheckList className="mt-8" icon="minus" items={t.avoid.items} /></Reveal>
          {t.callouts?.map(c => (
            <Reveal key={c.title} delay={0.1}>
              <div className="mt-6 flex gap-3 rounded-2xl border border-bv-blue-line bg-bv-base p-5 text-[15px] leading-7 text-bv-ink2">
                <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-bv-blue" />
                <div>
                  <p className="font-semibold text-bv-ink">{c.title}</p>
                  <p className="mt-1">{c.body}</p>
                  {c.source && <a href={c.source.url} target="_blank" rel="noopener noreferrer nofollow" className="mt-2 inline-flex items-center gap-1 text-[14px] font-semibold text-bv-blue hover:underline">{c.source.label} <ExternalLink className="h-3.5 w-3.5" /></a>}
                </div>
              </div>
            </Reveal>
          ))}
        </Section>

        <Section tone="dark" id="run-it-in-beatview" labelledBy="beatview-title">
          <SectionHeading tone="dark" id="beatview-title" eyebrow={t.beatview.label ?? 'Run it in Beatview'} title={t.beatview.title} description={t.beatview.body} />
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="bv-shine h-[54px] rounded-xl px-7 text-base font-semibold"><SmartLink href={APP_URL}>Start for free <ArrowRight className="h-4 w-4" /></SmartLink></Button>
            <Button asChild size="lg" variant="outline" className="h-[54px] rounded-xl border-white/20 bg-white/5 px-7 text-base font-semibold text-white hover:bg-white/10 hover:text-white"><SmartLink href="/ai-interviews"><Video className="h-4 w-4" /> How AI interviews work</SmartLink></Button>
          </div>
        </Section>

        <FaqSection title={`${t.name}: frequently asked questions`} items={t.faqs} idPrefix={`${t.slug}-faq`} tone="base" />

        <Section tone="white" narrow labelledBy="download-title">
          <SectionHeading id="download-title" eyebrow="Download" title={`Get the ${t.name.toLowerCase()} template`} />
          <Reveal delay={0.05}>
            <div className="mt-8 flex flex-col gap-5 rounded-3xl border border-bv-blue-line bg-bv-base p-6 sm:flex-row sm:items-center sm:p-8">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-bv-blue-wash text-bv-blue">{t.file.format === 'CSV' ? <FileSpreadsheet className="h-6 w-6" /> : <FileText className="h-6 w-6" />}</span>
              <div className="flex-1">
                <p className="text-[15px] leading-7 text-bv-ink2">{t.file.summary}</p>
                <p className="mt-1 text-[13px] text-bv-mute">{t.file.format === 'CSV' ? 'Opens in Excel, Google Sheets or Numbers.' : 'Opens in any text editor, or paste it into Word or Google Docs.'} Version {formatDate(t.updated)}.</p>
              </div>
              <DownloadButton t={t} />
            </div>
            {t.sources && t.sources.length > 0 && (
              <div className="mt-6 flex gap-3 rounded-2xl border border-bv-blue-line bg-white p-5 text-[13.5px] leading-6 text-bv-ink2">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-bv-blue" />
                <div>
                  <p>Sources: {t.sources.map((s, i) => <span key={s.url}>{i > 0 && '; '}<a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="font-semibold text-bv-blue hover:underline">{s.label}</a></span>)}. This template is general guidance, not legal advice.</p>
                </div>
              </div>
            )}
          </Reveal>
        </Section>
      </article>

      <RelatedLinks eyebrow="More templates" title="Related templates and guides" tone="base" items={related} />
      <CtaPanel eyebrow="Start free" title={<>Put the template to work <span className="bv-hero-highlight">on a live role.</span></>} description="Beatview screens every application against your criteria and interviews the shortlist with the same structured questions. The Free plan covers one active job; AI interviews are on Pro." secondary={{ label: section.allLabel, href: section.href }} />
    </PageShell>
  );
}

export function TemplateDetailPage({ slug }: { slug: string }) {
  const t = getTemplate(slug);
  return t ? <TemplatePage t={t} /> : <NotFound />;
}

export default function TemplateDetailRoute() {
  const { template } = useParams<{ template: string }>();
  return <TemplateDetailPage slug={template ?? ''} />;
}

